import React, { useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import cn from 'classnames';
import { useTranslation, Trans } from 'react-i18next';
import YadLogo from '../../assets/icons/YadLogo';
import InfoText from '../atoms/InfoText';
import { CloseCircle } from 'iconsax-react';

import CustomModal from '../atoms/CustomModal';
import { setModalId, usePublicState } from '../../store/slices/publicSlice';
import {
  setProductFilters,
  resetProductFilters,
  useProductsState,
  ProductTag,
} from '../../store/slices/ProductsSlice';
import Button from '../atoms/Button';
import FormField from '../FormField/FormField';

const schema = z.object({
  fromDate: z.any().optional(),
  toDate: z.any().optional(),
  tags: z.array(z.nativeEnum(ProductTag)).optional(),
});

type SchemaMeta = z.infer<typeof schema>;

export default function FiltersModal() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { modalId } = usePublicState();
  const { filters } = useProductsState();

  const isModalOpened = modalId === 'PRODUCTS_FILTERS';

  const methods = useForm<SchemaMeta>({
    resolver: zodResolver(schema),
    defaultValues: {
      fromDate: undefined,
      toDate: undefined,
      tags: [],
    },
  });

  const selectedTags =
    useWatch({
      name: 'tags',
      control: methods.control,
    }) || [];

  const handleTagToggle = (tagValue: ProductTag) => {
    const currentTags = [...selectedTags];
    const index = currentTags.indexOf(tagValue);
    if (index > -1) {
      currentTags.splice(index, 1);
    } else {
      currentTags.push(tagValue);
    }
    methods.setValue('tags', currentTags);
  };

  useEffect(() => {
    if (filters && isModalOpened) {
      methods.reset({
        fromDate: filters.fromDate ? dayjs(filters.fromDate) : undefined,
        toDate: filters.toDate ? dayjs(filters.toDate) : undefined,
        tags: filters.tags || [],
      });
    }
  }, [filters, isModalOpened, methods]);

  const onClose = () => {
    dispatch(setModalId(null));
  };

  const onSubmit = (values: SchemaMeta) => {
    dispatch(
      setProductFilters({
        fromDate: values.fromDate
          ? dayjs(values.fromDate).format('YYYY-MM-DD')
          : undefined,
        toDate: values.toDate
          ? dayjs(values.toDate).format('YYYY-MM-DD')
          : undefined,
        tags: values.tags && values.tags.length > 0 ? values.tags : undefined,
      })
    );
    onClose();
  };

  const handleReset = () => {
    methods.reset({
      fromDate: undefined,
      toDate: undefined,
      tags: [],
    });
    dispatch(resetProductFilters());
  };

  const tagsList = [
    { label: t('product.tagReturned', 'Returned'), value: ProductTag.RETURNED },
    { label: t('product.tagNew', 'New'), value: ProductTag.NEW },
    { label: t('product.tagRejected', 'Rejected'), value: ProductTag.REJECTED },
  ];

  return (
    <CustomModal
      isOpen={isModalOpened}
      onClose={onClose}
      title=""
      closable={false}
      centered
    >
      <div className="space-y-6" data-testid="filters-modal-wrapper">
        <div
          className="flex justify-between items-start mb-2"
          data-testid="filters-modal-header"
        >
          <div className="space-y-2">
            <div
              className="flex items-center gap-2"
              data-testid="filters-modal-logo-wrapper"
            >
              <YadLogo width={50} height={50} />
              <InfoText
                text={t('product.filterTitle', 'Products filter')}
                size="2xl"
                className="!font-bold !text-light-button-primary"
              />
            </div>

            <InfoText
              size="sm"
              className="text-[#777777]"
              text={
                (
                  <Trans
                    i18nKey="product.filterDesc"
                    defaults="Submit your requirements to <0>Vanguard Industrial Supply</0>. We'll respond within 24 hours."
                    components={[
                      <strong className="text-[#090909] font-bold" />,
                    ]}
                  />
                ) as any
              }
            />
          </div>

          <button
            onClick={onClose}
            type="button"
            className="bg-[#F9F9F9] dark:bg-gray-800 w-12 h-12 rounded-full flex items-center justify-center shrink-0 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            data-testid="filters-modal-close-btn"
          >
            <CloseCircle
              size={24}
              variant="Bold"
              color="#090909"
              className="bg-white dark:bg-transparent rounded-full"
            />
          </button>
        </div>

        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onSubmit)}
            className="space-y-6"
            data-testid="filters-modal-form"
          >
            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
              data-testid="filters-modal-date-grid"
            >
              <FormField
                id="fromDate"
                name="fromDate"
                type="date"
                label={t('home.dateFrom')}
                placeholder={t('product.enter', 'enter')}
                maxDate={dayjs()}
                suffixIcon={null}
                labelClassName="!text-base !font-semibold !leading-none !text-[#090909]"
                className="!rounded-xl"
              />

              <FormField
                id="toDate"
                name="toDate"
                type="date"
                label={t('home.dateTo')}
                placeholder={t('product.enter', 'enter')}
                maxDate={dayjs()}
                suffixIcon={null}
                labelClassName="!text-base !font-semibold !leading-none !text-[#090909]"
                className="!rounded-2xl"
              />
            </div>

            <div className="space-y-2">
              <span
                className="block text-base !font-semibold leading-none text-[#090909] dark:text-gray-100"
                data-testid="filters-modal-tag-label"
              >
                {t('product.tagLabel', 'Product tag')}
              </span>
              <div
                className="flex flex-wrap gap-2"
                data-testid="filters-modal-tags"
              >
                {tagsList.map((tag) => (
                  <button
                    key={tag.value}
                    type="button"
                    onClick={() => handleTagToggle(tag.value)}
                    className={cn(
                      'px-4 py-2 rounded-lg border text-base !font-normal leading-none transition-colors duration-200',
                      selectedTags.includes(tag.value)
                        ? 'bg-light-success !text-white'
                        : 'bg-white text-[#111928] border-gray-200 hover:border-gray-400 dark:bg-transparent dark:text-gray-300 dark:border-gray-700 dark:hover:border-gray-500'
                    )}
                    data-testid={`filters-modal-tag-${tag.value}`}
                  >
                    {tag.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Footer Actions */}
            <div
              className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-800"
              data-testid="filters-modal-footer"
            >
              <Button
                variant="outline"
                type="button"
                onClick={handleReset}
                className="!rounded-xl !border-light-input-border !text-light-text-primary !font-semibold !px-6 !py-3"
                size="md"
                data-testid="filters-modal-reset-btn"
              >
                {t('product.resetFilter', 'Reset filter')}
              </Button>
              <Button
                size="md"
                type="submit"
                className="!rounded-xl !font-semibold !px-12 !py-3 !bg-[#141414] !text-white hover:!bg-gray-900 border-0 dark:!bg-white dark:!text-black dark:hover:!bg-gray-100"
                data-testid="filters-modal-submit-btn"
              >
                {t('product.filterBtn', 'Filter')}
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </CustomModal>
  );
}
